import { Link } from 'react-router-dom'
import { Card, CardHeader } from '../ui/Card'
import { Skeleton } from '../ui/Skeleton'
import type { OrganisationSetupProgress, StoreSetupProgress } from '../../services/setupProgressAdminApi'

function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? (completed / total) * 100 : 0
  return (
    <div className="flex min-w-[6rem] flex-1 items-center gap-2">
      <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {completed}/{total}
      </span>
    </div>
  )
}

function StepList({ steps }: { steps: Array<{ id: string; title: string; complete: boolean; optional?: boolean }> }) {
  return (
    <ul className="space-y-2">
      {steps.map((step) => (
        <li key={step.id} className="flex items-start gap-2 text-sm">
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
              step.complete
                ? 'bg-primary text-primary-foreground'
                : 'border border-border bg-muted text-muted-foreground'
            }`}
            aria-hidden
          >
            {step.complete ? '✓' : ''}
          </span>
          <span className={step.complete ? 'text-foreground' : 'text-muted-foreground'}>
            {step.title}
            {step.optional ? <span className="ml-1 text-xs text-muted-foreground">(optional)</span> : null}
          </span>
        </li>
      ))}
    </ul>
  )
}

function StoreProgressBlock({
  store,
  organisationId,
  compact = false,
}: {
  store: StoreSetupProgress
  organisationId?: string | number
  compact?: boolean
}) {
  const incompleteRequired = store.steps.filter((s) => !s.complete && !s.optional)
  return (
    <div className={compact ? 'rounded-xl border border-border bg-card/40 p-4' : 'space-y-3'}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium text-foreground">{store.storeName}</p>
          <p className="text-xs text-muted-foreground">{store.subdomain || '—'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ProgressBar completed={store.completedCount} total={store.totalCount} />
          {store.checklistDismissedAt ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Checklist dismissed
            </span>
          ) : null}
          {organisationId ? (
            <Link
              to={`/organizations/${organisationId}/stores/${store.storeId}`}
              className="text-xs font-medium text-primary hover:underline"
            >
              Store detail
            </Link>
          ) : null}
        </div>
      </div>
      {!compact ? (
        <StepList steps={store.steps} />
      ) : incompleteRequired.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Still needed: {incompleteRequired.map((s) => s.title).join(', ')}
        </p>
      ) : (
        <p className="text-xs text-emerald-700 dark:text-emerald-300">Merchant checklist complete.</p>
      )}
    </div>
  )
}

type PlatformStoreSetupProgressProps = {
  data: OrganisationSetupProgress | null
  loading?: boolean
  error?: string | null
  organisationId: string | number
  compactStores?: boolean
}

export function PlatformStoreSetupProgress({
  data,
  loading = false,
  error = null,
  organisationId,
  compactStores = false,
}: PlatformStoreSetupProgressProps) {
  return (
    <Card>
      <CardHeader
        title="Store setup progress"
        description="Merchant onboarding checklist and platform steps — use this to see who needs help before go-live."
      />
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {loading ? (
        <div className="space-y-4" aria-busy="true" aria-label="Loading setup progress">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">No setup progress available.</p>
      ) : (
        <div className="space-y-6">
          <section>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-foreground">Platform steps</h3>
              <ProgressBar completed={data.platform.completedCount} total={data.platform.totalCount} />
            </div>
            <StepList steps={data.platform.steps} />
          </section>

          <section className="border-t border-border pt-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-foreground">Merchant store checklist</h3>
              <span className="text-xs text-muted-foreground">
                {data.stores.length} store{data.stores.length === 1 ? '' : 's'}
              </span>
            </div>
            {data.stores.length === 0 ? (
              <p className="text-sm text-muted-foreground">No stores yet.</p>
            ) : (
              <div className="space-y-3">
                {data.stores.map((store) => (
                  <StoreProgressBlock
                    key={store.storeId}
                    store={store}
                    organisationId={organisationId}
                    compact={compactStores}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </Card>
  )
}

export function SingleStoreSetupProgress({
  data,
  loading = false,
  error = null,
}: {
  data: StoreSetupProgress | null
  loading?: boolean
  error?: string | null
}) {
  return (
    <Card>
      <CardHeader
        title="Merchant setup checklist"
        description="Same steps the shop owner sees in store admin — incomplete items are good outreach targets."
      />
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {loading ? (
        <div className="space-y-3" aria-busy="true" aria-label="Loading setup progress">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">No setup progress available.</p>
      ) : (
        <div className="space-y-4">
          <ProgressBar completed={data.completedCount} total={data.totalCount} />
          {data.checklistDismissedAt ? (
            <p className="text-xs text-muted-foreground">
              Merchant dismissed the dashboard checklist on{' '}
              {new Date(data.checklistDismissedAt).toLocaleString()}.
            </p>
          ) : null}
          <StepList steps={data.steps} />
        </div>
      )}
    </Card>
  )
}
